import { getWindowObject } from '@eventbrite/legacy-infra.feature-detection';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import type { StatsigOptions, StatsigUser } from '@eventbrite/legacy-infra.statsig';
import { DEFAULT_SAMPLE_RATE, STATSIG_ENV } from './constants';
import { getDeviceData } from './device';
import type { StatsigEnvironments, UserMetadata, UserMetadataField } from './types';
import { Stage } from '@eventbrite/webapp-constants';

const addValueIfExists = <T>(key: string, value: T) => (value ? { [key]: value } : {});

export const getUser = (userOptions?: StatsigUser, userFields?: Set<UserMetadataField>) => {
    const { userAgent } = getWindowObject('navigator') ?? {};
    const deviceData = getDeviceData({ userAgent });

    const publicUserOptions: StatsigUser = {
        ...addValueIfExists('userID', userOptions?.userID),
        ...addValueIfExists('ip', userOptions?.ip),
        ...addValueIfExists('country', userOptions?.country),
        ...addValueIfExists('locale', userOptions?.locale),
        ...addValueIfExists('appVersion', userOptions?.appVersion),
        ...addValueIfExists('custom', userOptions?.custom),
        ...addValueIfExists('privateAttributes', userOptions?.privateAttributes),
        ...addValueIfExists('customIDs', userOptions?.customIDs),
    };

    const userMetadata: UserMetadata = {
        ...(userAgent ? { userAgent } : {}),
        ...(deviceData ? { ...deviceData } : {}),
        ...publicUserOptions,
        // Anything not explicitly set at the top level should be private for PII reasons
        privateAttributes: {
            ...(userOptions?.privateAttributes ?? {}),
            ...omit(userOptions ?? {}, ...Object.keys(publicUserOptions ?? {})),
        },
    };

    if (!userFields) {
        return userMetadata;
    } else {
        return pick(userMetadata, Array.from(userFields));
    }
};

export const getStatsigEnv = (stage: Stage): StatsigEnvironments => {
    if (stage === Stage.PROD) {
        return STATSIG_ENV.PROD;
    }
    if (stage === Stage.TEST) {
        return STATSIG_ENV.QA;
    }
    if (stage === Stage.DEV) {
        return STATSIG_ENV.DEV;
    }
    return;
};

export const getOptions = (options?: StatsigOptions) => ({
    environment: {
        tier: options?.environment?.tier ?? STATSIG_ENV.DEV,
    },
    ...options,
});

export const shouldInitializeSDK = (
    sampleRate: number = DEFAULT_SAMPLE_RATE,
    deactivateSdk?: boolean,
) => !deactivateSdk && Math.random() * 100 < sampleRate;
